/**
 * Precise Minmaxify Dialog Fix
 * 
 * This script targets the exact structure of Minmaxify dialogs
 * to prevent them from appearing during add-to-cart operations.
 */

(function() {
  // Configuration
  const DEBUG = true;
  
  // Debug logging
  function log(message, data) {
    if (DEBUG) {
      console.log(`[MinmaxifyFix] ${message}`, data || '');
    }
  }
  
  // Initialize when DOM is loaded
  document.addEventListener('DOMContentLoaded', function() {
    log('Initializing precise Minmaxify dialog fix');
    
    // Set up observers for dialog appearance
    setupDialogObserver();
    
    // Intercept fetch calls to detect cart additions
    interceptFetchCalls();
    
    // Track checkout button clicks
    trackCheckoutClicks();
  });
  
  // Global variables to track state
  let isAddingToCart = false;
  let lastCheckoutClick = 0;
  
  // Set up observer for dialog appearance
  function setupDialogObserver() {
    // Create a mutation observer to watch for dialog additions
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          // Check each added node
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // Look for the dialog element or wrapper containing it
              if (isMinmaxifyDialog(node)) {
                handleMinmaxifyDialog(node);
              } else {
                // Check children for dialog
                const dialogs = node.querySelectorAll('div[role="dialog"].minmaxify-p, .minmaxify-p[aria-modal="true"]');
                dialogs.forEach(dialog => {
                  handleMinmaxifyDialog(dialog);
                });
              }
            }
          });
        }
      });
    });
    
    // Start observing the entire document
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    log('Dialog observer started');
    
    // Also run an immediate check for any existing dialogs
    checkForExistingDialogs();
  }
  
  // Check for existing dialogs
  function checkForExistingDialogs() {
    const dialogs = document.querySelectorAll('div[role="dialog"].minmaxify-p, .minmaxify-p[aria-modal="true"]');
    dialogs.forEach(dialog => {
      handleMinmaxifyDialog(dialog);
    });
  }
  
  // Check if an element is a Minmaxify dialog
  function isMinmaxifyDialog(element) {
    return element.classList && 
           element.classList.contains('minmaxify-p') && 
           (element.getAttribute('role') === 'dialog' || 
            element.getAttribute('aria-modal') === 'true');
  }
  
  // Handle a detected Minmaxify dialog
  function handleMinmaxifyDialog(dialog) {
    log('Detected Minmaxify dialog:', dialog);
    
    // Get dialog content
    const content = dialog.textContent || '';
    
    // Check if this is a minimum quantity or order total message
    if (content.includes('minimum order quantity') || 
        content.includes('must have at least') ||
        content.includes('Cannot place order')) {
      
      log('Dialog contains validation message');
      
      // If we're in the process of adding to cart and this isn't a checkout attempt
      if (isAddingToCart && !isRecentCheckoutAttempt()) {
        log('Dialog appeared during add-to-cart, not during checkout');
        
        // Check if this is a minimum order total message
        if (content.includes('Must have at least $100 in total') || 
            content.includes('minimum order total')) {
          log('Minimum order total message during add-to-cart, dismissing');
          dismissDialog(dialog);
          return;
        }
        
        // For product minimum quantity messages, check if already fixed
        if (content.includes('must have at least') || content.includes('Must have at least')) {
          // Find bullet points or list items that contain product info
          const listItems = dialog.querySelectorAll('li, ul li');
          
          if (listItems.length > 0) {
            log('Found list items in dialog, checking product quantities');
            
            // Process each product error
            processProductErrors(listItems, dialog);
          } else {
            // Try to extract from general text
            const matches = content.match(/([^:]+):\s*Must have at least (\d+) of this item/i);
            if (matches && matches[1] && matches[2]) {
              log('Extracted product info from text:', matches[1], matches[2]);
              
              checkProductInCart(matches[1].trim(), parseInt(matches[2], 10))
                .then(hasCorrectQuantity => {
                  if (hasCorrectQuantity) {
                    log('Product already has correct quantity, dismissing dialog');
                    dismissDialog(dialog);
                  }
                });
            }
          }
        }
      }
    }
  }
  
  // Process product error messages
  function processProductErrors(listItems, dialog) {
    // Convert to array and process
    const items = Array.from(listItems);
    const productChecks = [];
    
    items.forEach(item => {
      const text = item.textContent || '';
      const matches = text.match(/([^:]+):\s*Must have at least (\d+) of this item/i);
      
      if (matches && matches[1] && matches[2]) {
        const productName = matches[1].trim();
        const minQuantity = parseInt(matches[2], 10);
        
        log('Found product error:', productName, minQuantity);
        
        // Create promise for checking this product
        const check = checkProductInCart(productName, minQuantity);
        productChecks.push(check);
      }
    });
    
    // If we found any product errors, check if all are already satisfied
    if (productChecks.length > 0) {
      Promise.all(productChecks)
        .then(results => {
          // If all products have correct quantities, dismiss dialog
          if (results.every(result => result === true)) {
            log('All products already have correct quantities, dismissing dialog');
            dismissDialog(dialog);
          }
        });
    }
  }
  
  // Check if a product already has the correct quantity in cart
  function checkProductInCart(productName, minQuantity) {
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const items = cart.items || [];
        
        // Find matching item in cart
        const cartItem = items.find(item => {
          return item.title.includes(productName) || 
                 productName.includes(item.title);
        });
        
        // Check if quantity meets minimum
        const hasCorrectQuantity = cartItem && cartItem.quantity >= minQuantity;
        log(`Product "${productName}" in cart: ${Boolean(cartItem)}, quantity: ${cartItem ? cartItem.quantity : 0}, needs: ${minQuantity}`);
        
        return hasCorrectQuantity;
      })
      .catch(error => {
        log('Error checking cart:', error);
        return false;
      });
  }
  
  // Dismiss a dialog
  function dismissDialog(dialog) {
    // First try to click the close button
    const closeButton = dialog.querySelector('.minmaxify-close, button[title="Close (Esc)"]');
    
    if (closeButton) {
      log('Found close button, clicking it');
      closeButton.click();
      return;
    }
    
    // If no close button, try to hide the dialog
    log('No close button found, hiding dialog directly');
    dialog.style.display = 'none';
    
    // Also hide wrapper and background
    const wrapper = document.querySelector('.minmaxify-p-wrap');
    const background = document.querySelector('.minmaxify-p-bg.minmaxify-a');
    
    if (wrapper) {
      wrapper.style.display = 'none';
    }
    
    if (background) {
      background.style.display = 'none';
    }
    
    // Reset body styles
    document.body.classList.remove('modal-open', 'overflow-hidden');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
  
  // Intercept fetch calls to detect cart additions
  function interceptFetchCalls() {
    // Save original fetch
    const originalFetch = window.fetch;
    
    // Replace with our version
    window.fetch = function(url, options) {
      // Check if this is a cart addition
      if (url && typeof url === 'string' && url.includes('/cart/add')) {
        log('Detected cart addition fetch call');
        
        // Set flag that we're adding to cart
        isAddingToCart = true;
        
        // Reset flag after a delay (longer than the typical request time)
        setTimeout(() => {
          isAddingToCart = false;
        }, 3000);
      }
      
      // Call original fetch
      return originalFetch.apply(this, arguments);
    };
  }
  
  // Track checkout button clicks
  function trackCheckoutClicks() {
    document.addEventListener('click', function(event) {
      const target = event.target;
      
      // Check if this is a checkout button
      const isCheckoutButton = 
        (target.classList && target.classList.contains('cart__checkout')) ||
        (target.getAttribute('name') === 'checkout') ||
        (target.textContent && target.textContent.trim().toLowerCase() === 'checkout') ||
        (target.closest('button[name="checkout"]') !== null);
      
      if (isCheckoutButton) {
        log('Checkout button clicked');
        lastCheckoutClick = Date.now();
      }
    }, true);
  }
  
  // Check if there was a recent checkout attempt
  function isRecentCheckoutAttempt() {
    // Consider a checkout click within the last 5 seconds as recent
    return (Date.now() - lastCheckoutClick) < 5000;
  }
})();